import React, { useState, useEffect } from "react";
import {
  FaUserCog,
  FaEdit,
  FaSave,
  FaTimes,
  FaShieldAlt,
  FaUsers,
  FaEnvelope,
  FaClock,
  FaExclamationTriangle,
} from "react-icons/fa";
import {
  getAllAdminUsers,
  updateUserRole,
  getUserRole,
} from "../firebase";
import {
  logAdminActivity,
  getCurrentUserInfo,
} from "../utils/adminSecurity";
import {
  ROLES,
  getRoleDisplayName,
  getRoleDescription,
  getAvailableRoles,
  hasPermission,
  PERMISSIONS,
} from "../utils/rolePermissions";
import "./UserManagement.css";

const UserManagement = ({ userRole }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");

  // Check if current user can manage roles
  const canManageRoles = hasPermission(userRole, PERMISSIONS.MANAGE_ROLES);
  const canViewUsers = hasPermission(userRole, PERMISSIONS.MANAGE_USERS);

  useEffect(() => {
    if (canViewUsers) {
      fetchUsers();
    } else {
      setError("Bu sayfaya erişim yetkiniz bulunmamaktadır.");
      setLoading(false);
    }
  }, [canViewUsers]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersData = await getAllAdminUsers();
      setUsers(usersData);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Kullanıcılar yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditRole = (user) => {
    if (!canManageRoles) {
      alert("Rol değiştirme yetkiniz bulunmamaktadır.");
      return;
    }

    const currentUserInfo = getCurrentUserInfo();
    if (user.uid === currentUserInfo?.uid) {
      alert("Kendi rolünüzü değiştiremezsiniz.");
      return;
    }

    setEditingUser(user);
    setSelectedRole(user.role || ROLES.MARKETING);
    setError("");
  };

  const handleSaveRole = async () => {
    if (!editingUser || !selectedRole) return;

    try {
      setUpdating(true);
      setError("");

      await updateUserRole(editingUser.uid, selectedRole);

      // Log the role change
      logAdminActivity("user_role_changed", {
        targetUserId: editingUser.uid,
        targetUserEmail: editingUser.email,
        oldRole: editingUser.role,
        newRole: selectedRole,
        timestamp: new Date().toISOString(),
      });

      // Update local state
      setUsers(users.map(user =>
        user.uid === editingUser.uid
          ? { ...user, role: selectedRole }
          : user
      ));

      setEditingUser(null);
      setSelectedRole("");

      alert(`${editingUser.email} kullanıcısının rolü ${getRoleDisplayName(selectedRole)} olarak güncellendi.`);

    } catch (error) {
      console.error("Error updating user role:", error);
      setError("Rol güncellenirken hata oluştu.");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setSelectedRole("");
    setError("");
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Bilinmiyor";

    let date;
    if (timestamp.toDate) {
      // Firestore Timestamp
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      // Firestore Timestamp object
      date = new Date(timestamp.seconds * 1000);
    } else {
      // Regular timestamp or date string
      date = new Date(timestamp);
    }

    return date.toLocaleString('tr-TR');
  };

  if (!canViewUsers) {
    return (
      <div className="user-management">
        <div className="access-denied">
          <FaExclamationTriangle size={48} />
          <h2>Erişim Engellendi</h2>
          <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
          <p>Sadece admin rolündeki kullanıcılar kullanıcı yönetimi sayfasını görüntüleyebilir.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading">
          <FaUserCog size={32} />
          <p>Kullanıcılar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <div className="header-content">
          <FaUserCog size={24} />
          <div>
            <h2>Kullanıcı Yönetimi</h2>
            <p>Admin kullanıcılarını ve rollerini yönetin</p>
          </div>
        </div>
        <div className="stats">
          <div className="stat-item">
            <FaUsers className="stat-icon" />
            <div>
              <span className="stat-number">{users.length}</span>
              <span className="stat-label">Toplam Kullanıcı</span>
            </div>
          </div>
          <div className="stat-item">
            <FaShieldAlt className="stat-icon" />
            <div>
              <span className="stat-number">
                {users.filter(u => u.role === ROLES.ADMIN).length}
              </span>
              <span className="stat-label">Admin</span>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <FaExclamationTriangle />
          <span>{error}</span>
        </div>
      )}

      <div className="role-info">
        <h3>Rol Açıklamaları</h3>
        <div className="role-cards">
          {getAvailableRoles().map(role => (
            <div key={role} className={`role-card role-${role}`}>
              <h4>{getRoleDisplayName(role)}</h4>
              <p>{getRoleDescription(role)}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="users-section">
        <h3>Kullanıcılar ({users.length})</h3>

        {users.length === 0 ? (
          <div className="no-users">
            <FaUsers size={48} />
            <p>Henüz kayıtlı admin kullanıcısı bulunmuyor.</p>
          </div>
        ) : (
          <div className="users-table">
            <div className="table-header">
              <div className="header-cell">Kullanıcı</div>
              <div className="header-cell">Rol</div>
              <div className="header-cell">Son Giriş</div>
              <div className="header-cell">2FA Durumu</div>
              {canManageRoles && <div className="header-cell">İşlemler</div>}
            </div>

            {users.map(user => (
              <div key={user.uid} className="table-row">
                <div className="cell user-info">
                  <FaEnvelope className="user-icon" />
                  <div>
                    <span className="email">{user.email}</span>
                    <span className="uid">ID: {user.uid.slice(0, 8)}...</span>
                  </div>
                </div>

                <div className="cell role-cell">
                  {editingUser?.uid === user.uid ? (
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="role-select"
                      disabled={updating}
                    >
                      {getAvailableRoles().map(role => (
                        <option key={role} value={role}>
                          {getRoleDisplayName(role)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={`role-badge role-${user.role || 'marketing'}`}>
                      {getRoleDisplayName(user.role || 'marketing')}
                    </span>
                  )}
                </div>

                <div className="cell">
                  <FaClock className="time-icon" />
                  {formatDate(user.lastLogin)}
                </div>

                <div className="cell">
                  <span className={`status-badge ${user.twoFactorEnabled ? 'enabled' : 'disabled'}`}>
                    {user.twoFactorEnabled ? '✓ Aktif' : '✗ Pasif'}
                  </span>
                </div>

                {canManageRoles && (
                  <div className="cell actions-cell">
                    {editingUser?.uid === user.uid ? (
                      <div className="action-buttons">
                        <button
                          className="save-btn"
                          onClick={handleSaveRole}
                          disabled={updating}
                        >
                          {updating ? <FaClock /> : <FaSave />}
                          {updating ? 'Kaydediliyor...' : 'Kaydet'}
                        </button>
                        <button
                          className="cancel-btn"
                          onClick={handleCancelEdit}
                          disabled={updating}
                        >
                          <FaTimes />
                          İptal
                        </button>
                      </div>
                    ) : (
                      <button
                        className="edit-btn"
                        onClick={() => handleEditRole(user)}
                        title="Rolü Düzenle"
                      >
                        <FaEdit />
                        Düzenle
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {!canManageRoles && (
        <div className="info-message">
          <FaExclamationTriangle />
          <span>
            Kullanıcı rollerini değiştirmek için admin yetkisine sahip olmanız gerekiyor.
          </span>
        </div>
      )}
    </div>
  );
};

export default UserManagement;